import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { IconPaths, KTCard, ModalSize } from '_metronic/helpers'
import {
  Button,
  DetailField,
  DetailFields,
  DetailPanel,
  DetailSection,
  InfoAlert,
  PageHeading,
  StatusDot,
  StatusDotTone,
  TableEmptyState,
  useModal,
} from 'app/components'
import {
  MSG_SEM_PERMISSAO_CORRIGIR_LOTACAO,
  usePermissaoCorrigirLotacao,
} from '../core/_esusEnvioPermissao'
import { parseMensagemErro, temErroDeLotacaoCorrigivel } from '../core/_esusErro'
import { VAZIO, formatarData, formatarDataHora } from '../core/_formato'
import { EsusEnvioStatus, rotuloStatus, rotuloTipoFicha } from '../core/_models'
import { getEsusEnvio } from '../core/_requests'
import { podeReenviarStatus, useReenvioEsus } from '../core/useReenvioEsus'
import { EsusCorrigirLotacaoModal } from './EsusCorrigirLotacaoModal'

/** Tom do status: texto + ponto, a mesma leitura da listagem (a cor só reforça). */
const TOM_STATUS: Record<EsusEnvioStatus, StatusDotTone> = {
  PENDENTE: 'warning',
  ENVIANDO: 'info',
  ENVIADO: 'success',
  ERRO: 'danger',
}

export const EsusEnvioDetalhePage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showModal } = useModal()
  const { podeReenviar, reenviando, reenviar } = useReenvioEsus()
  const podeCorrigirLotacao = usePermissaoCorrigirLotacao()

  const envioId = Number(id)
  const voltar = () => navigate('/apps/esus/lista')

  const {
    data: envio,
    isLoading,
    refetch,
  } = useQuery(['esus-envio-detalhe', envioId], () => getEsusEnvio(envioId), {
    enabled: !!envioId,
    cacheTime: 0,
    refetchOnWindowFocus: false,
  })

  // `parseMensagemErro` já sanitiza. Nada mais do payload de erro é renderizado — nem o objeto de
  // resposta, nem `JSON.stringify` (AC2).
  const linhasErro = useMemo(() => parseMensagemErro(envio?.mensagemErro), [envio?.mensagemErro])

  const mostrarReenviar = podeReenviar && podeReenviarStatus(envio?.status)

  // AC4: a ação só existe quando trocar a lotação é o conserto certo (campos `lotacao`/`lotacao.cbo`)
  // e enquanto o envio não estiver ENVIADO.
  const mostrarCorrigirLotacao =
    !!envio?.atendimentoId &&
    envio?.status !== 'ENVIADO' &&
    temErroDeLotacaoCorrigivel(linhasErro) &&
    podeCorrigirLotacao

  const abrirCorrecaoDeLotacao = () => {
    if (!envio?.atendimentoId) return
    showModal({
      title: 'Corrigir lotação do atendimento',
      size: ModalSize.Medium,
      content: (
        <EsusCorrigirLotacaoModal
          atendimentoId={envio.atendimentoId}
          aoCorrigir={async () => {
            // A confirmação do reenvio (AC3) vale aqui também: quem cancela fica com a lotação
            // corrigida e o envio ainda em ERRO — por isso recarrega dos dois jeitos.
            await reenviar(envioId)
            refetch()
          }}
          aoAtualizar={async () => {
            await refetch()
          }}
        />
      ),
    })
  }

  if (isLoading) {
    return (
      <KTCard>
        <div className='card-body'>
          <DetailPanel isLoading loadingLabel='Carregando envio…' />
        </div>
      </KTCard>
    )
  }

  if (!envio) {
    return (
      <KTCard>
        <div className='card-body'>
          <TableEmptyState
            title='Envio não encontrado ou fora do seu acesso.'
            action={{ label: 'Voltar para a lista', onClick: voltar }}
          />
        </div>
      </KTCard>
    )
  }

  return (
    <>
      <PageHeading
        title={`Envio nº ${envio.id}`}
        description={[rotuloTipoFicha(envio), envio.empresaNome].filter(Boolean).join(' · ')}
        actions={
          <div className='d-flex flex-wrap gap-2'>
            {mostrarCorrigirLotacao && (
              <Button
                variant='secondary-outline'
                onClick={abrirCorrecaoDeLotacao}
                icon={IconPaths.Pen}
              >
                Corrigir lotação e reenviar
              </Button>
            )}
            {mostrarReenviar && (
              <Button
                className='btn-primary'
                disabled={reenviando}
                onClick={() => reenviar(envioId, refetch)}
                icon={IconPaths.Refresh}
              >
                Reenviar
              </Button>
            )}
          </div>
        }
      />

      <KTCard>
        <div className='card-body'>
          <DetailPanel>
            <DetailSection title='Envio'>
              <DetailFields>
                <DetailField
                  label='Status'
                  value={
                    <StatusDot
                      label={rotuloStatus(envio)}
                      tone={TOM_STATUS[envio.status as EsusEnvioStatus] ?? 'neutral'}
                    />
                  }
                />
                <DetailField label='Tipo de ficha' value={rotuloTipoFicha(envio)} />
                <DetailField label='Tentativas' value={envio.tentativas ?? 0} />
                <DetailField label='Enviado em' value={formatarDataHora(envio.dhEnvio)} />
                <DetailField
                  label='Próxima tentativa automática'
                  value={
                    envio.dhProximaTentativa
                      ? formatarDataHora(envio.dhProximaTentativa)
                      : 'Fora da fila automática'
                  }
                />
                <DetailField label='UUID da ficha' value={envio.uuidFicha || VAZIO} wide />
              </DetailFields>
            </DetailSection>

            <DetailSection title='Atendimento'>
              <DetailFields>
                <DetailField label='Instituição' value={envio.empresaNome || VAZIO} />
                <DetailField label='Atendimento' value={envio.atendimentoId ?? VAZIO} />
                <DetailField
                  label='Data do atendimento'
                  value={formatarData(envio.atendimentoData)}
                />
                <DetailField label='Paciente' value={envio.pacienteNome || VAZIO} />
                <DetailField label='Unidade' value={envio.unidadeNome || VAZIO} />
              </DetailFields>
            </DetailSection>

            {linhasErro.length > 0 && (
              <DetailSection title='O que impediu o envio'>
                <div className='d-flex flex-column gap-4'>
                  {linhasErro.map((linha, indice) => (
                    <InfoAlert
                      key={`${linha.campo ?? 'generico'}-${indice}`}
                      variant='danger'
                      title={linha.rotulo || undefined}
                      className='mb-0'
                    >
                      {linha.rotulo ? (
                        <>
                          <div className='mb-2'>{linha.correcao}</div>
                          <div className='fs-7 text-gray-700'>
                            Onde corrigir: {linha.ondeCorrigir}
                          </div>
                        </>
                      ) : (
                        linha.texto
                      )}
                    </InfoAlert>
                  ))}

                  {/*
                    A mesma guarda por `atendimentoId` que condiciona o botão (AC3): numa ficha de
                    cadastro (`FCI`) não existe atendimento para corrigir, e o erro pode citar
                    `lotacao`/`lotacao.cbo` — que estão em `CAMPOS_LOTACAO_CORRIGIVEL` porque para a
                    FAI trocar a lotação é o conserto certo. Sem esta guarda o usuário seria mandado
                    pedir a permissão `Atendimento:corrigirLotacao`, que não conserta nada aqui. O
                    critério é a ausência de atendimento, nunca o nome do campo — por isso
                    `CAMPOS_LOTACAO_CORRIGIVEL` fica intocado.
                  */}
                  {!!envio.atendimentoId &&
                    temErroDeLotacaoCorrigivel(linhasErro) &&
                    !podeCorrigirLotacao && (
                      <InfoAlert variant='info' className='mb-0 fs-7'>
                        {MSG_SEM_PERMISSAO_CORRIGIR_LOTACAO}
                      </InfoAlert>
                    )}
                </div>
              </DetailSection>
            )}
          </DetailPanel>
        </div>
      </KTCard>
    </>
  )
}
